import { all, takeLatest, put, call } from 'redux-saga/effects';
import regularizationService, {
  toDateFromFirestoreTimestampField,
} from '../../services/regularization';
import { regularizationActionTypes } from './Regularization.actionTypes';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import moment from 'moment';

/** Renders an overlap-check result the same way approveRequestSaga already does. */
const formatOverlapDetails = (overlapCheck) =>
  (overlapCheck?.overlappingSessions || [])
    .map(
      (s) =>
        `${s.sessionStartDisplay} to ${s.sessionEndDisplay} (overlap: ${s.overlapHours}h ${s.overlapMinutes}m)`
    )
    .join(', ');

/** Turns a RegularizationError's `code` into a user-facing message; falls back to a generic one. */
const formatRegularizationErrorMessage = (error, fallbackMessage) => {
  switch (error?.code) {
    case 'TIME_OVERLAP':
      return `Time conflict detected: this time is already tracked by an existing session (${formatOverlapDetails(error.overlapCheck)}). Please choose a different time.`;
    case 'REQUEST_ALREADY_APPROVED':
      return 'This request has already been approved and can no longer be changed.';
    case 'REQUEST_NOT_FOUND':
      return 'This request no longer exists.';
    default:
      return error?.message || fallbackMessage;
  }
};

/**
 * Get user's regularization requests
 */
function* getRequestsSaga({ payload }) {
  try {
    const { userId, companyId } = payload;

    const requests = yield call(
      regularizationService.getUserRequests,
      userId,
      companyId
    );

    yield put({
      type: regularizationActionTypes.SET_STATE,
      payload: {
        requests,
        loading: false,
      },
    });
  } catch (error) {
    console.error('Error fetching regularization requests:', error);
    yield put({
      type: regularizationActionTypes.SET_STATE,
      payload: {
        requests: [],
        loading: false,
        error: error.message,
      },
    });
    addNotification({
      message: 'Failed to load regularization requests',
      level: notificationStates.error,
    });
  }
}

/**
 * Get manager's assigned regularization requests
 */
function* getManagerRequestsSaga({ payload }) {
  try {
    const { managerId, companyId } = payload;

    const requests = yield call(
      regularizationService.getManagerRequests,
      managerId,
      companyId
    );

    yield put({
      type: regularizationActionTypes.SET_STATE,
      payload: {
        managerRequests: requests,
        loading: false,
      },
    });
  } catch (error) {
    console.error('Error fetching manager regularization requests:', error);
    yield put({
      type: regularizationActionTypes.SET_STATE,
      payload: {
        managerRequests: [],
        loading: false,
        error: error.message,
      },
    });
    addNotification({
      message: 'Failed to load manager requests',
      level: notificationStates.error,
    });
  }
}

/**
 * Create a new regularization request
 */
function* createRequestSaga({ payload }) {
  let result;
  try {
    const requestData = yield call(
      regularizationService.createRequest,
      payload.requestData
    );

    yield put({
      type: regularizationActionTypes.SET_STATE,
      payload: {
        success: true,
      },
    });

    addNotification({
      message: 'Request submitted successfully',
      level: notificationStates.success,
    });

    // Refresh requests list
    yield put({
      type: regularizationActionTypes.GET_REQUESTS,
      payload: {
        userId: payload.requestData.userId,
        companyId: payload.requestData.companyID,
      },
    });

    result = { success: true, data: requestData };
    return result;
  } catch (error) {
    console.error('Error creating regularization request:', error);
    addNotification({
      message: formatRegularizationErrorMessage(
        error,
        'Failed to submit request'
      ),
      level: notificationStates.error,
    });
    result = { success: false, error: error.message, code: error.code };
    return result;
  } finally {
    if (typeof payload?.onComplete === 'function') {
      payload.onComplete(result ?? { success: false, error: 'Unknown error' });
    }
  }
}

/**
 * Update an existing regularization request
 */
function* updateRequestSaga({ payload }) {
  let result;
  try {
    const { requestId, updateData, companyId, oldAssignee, newAssignee } =
      payload;

    yield call(
      regularizationService.updateRequest,
      requestId,
      updateData,
      companyId,
      oldAssignee,
      newAssignee
    );

    addNotification({
      message: 'Request updated successfully',
      level: notificationStates.success,
    });

    // Refresh requests list
    yield put({
      type: regularizationActionTypes.GET_REQUESTS,
      payload: {
        userId: updateData.userId,
        companyId,
      },
    });

    result = { success: true };
    return result;
  } catch (error) {
    console.error('Error updating regularization request:', error);
    addNotification({
      message: formatRegularizationErrorMessage(
        error,
        'Failed to update request'
      ),
      level: notificationStates.error,
    });
    result = { success: false, error: error.message, code: error.code };
    return result;
  } finally {
    if (typeof payload?.onComplete === 'function') {
      payload.onComplete(result ?? { success: false, error: 'Unknown error' });
    }
  }
}

/**
 * Delete a regularization request
 */
function* deleteRequestSaga({ payload }) {
  let result;
  try {
    const { requestId, companyId, assigneeId, userId } = payload;

    yield call(
      regularizationService.deleteRequest,
      requestId,
      companyId,
      assigneeId
    );

    addNotification({
      message: 'Request deleted successfully',
      level: notificationStates.success,
    });

    // Refresh requests list
    yield put({
      type: regularizationActionTypes.GET_REQUESTS,
      payload: {
        userId,
        companyId,
      },
    });

    result = { success: true };
    return result;
  } catch (error) {
    console.error('Error deleting regularization request:', error);
    addNotification({
      message: formatRegularizationErrorMessage(
        error,
        'Failed to delete request'
      ),
      level: notificationStates.error,
    });
    result = { success: false, error: error.message, code: error.code };
    return result;
  } finally {
    if (typeof payload?.onComplete === 'function') {
      payload.onComplete(result ?? { success: false, error: 'Unknown error' });
    }
  }
}

/**
 * Approve a regularization request
 * Creates a session and updates request status
 */
function* approveRequestSaga({ payload }) {
  let result;
  try {
    const { requestData, companyId } = payload;

    const { teamId, userId } = requestData;
    const startDate = toDateFromFirestoreTimestampField(requestData.startTime);
    const endDate = toDateFromFirestoreTimestampField(requestData.endTime);
    if (!startDate || !endDate) {
      addNotification({
        message: 'Invalid request data: missing start/end time',
        level: notificationStates.error,
      });
      result = { success: false, error: 'Invalid request data' };
      return result;
    }
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    // Check for overlap
    const overlapCheck = yield call(
      regularizationService.checkSessionOverlap,
      teamId,
      userId,
      startMoment,
      endMoment
    );

    if (overlapCheck.hasOverlap) {
      const overlapDetails = overlapCheck.overlappingSessions
        .map(
          (s) =>
            `${s.sessionStartDisplay} to ${s.sessionEndDisplay} (overlap: ${s.overlapHours}h ${s.overlapMinutes}m)`
        )
        .join(', ');

      addNotification({
        message: `Time conflict detected: User already has tracked sessions. Overlapping sessions: ${overlapDetails}. Please resolve conflicts before approving.`,
        level: notificationStates.error,
      });
      result = { success: false, hasOverlap: true, overlapCheck };
      return result;
    }

    // Check 24 hours limit (dateId format: "day:month:year")
    const dateId = `${startMoment.date()}:${
      startMoment.month() + 1
    }:${startMoment.year()}`.replace(/(^|:)0+/g, '$1');
    const taskMinutes =
      (requestData.taskHours || 0) * 60 + (requestData.taskMinutes || 0);
    const isToday = moment(startMoment).isSame(moment(), 'day');

    const limitCheck = yield call(
      regularizationService.check24HoursLimit,
      teamId,
      userId,
      dateId,
      taskMinutes,
      isToday
    );

    if (!limitCheck.isValid) {
      addNotification({
        message: limitCheck.message || 'Time limit validation failed',
        level: notificationStates.error,
      });
      result = { success: false, limitExceeded: true, limitCheck };
      return result;
    }

    // Approve the request (creates session automatically)
    const data = yield call(
      regularizationService.approveRequest,
      requestData,
      companyId
    );

    addNotification({
      message: 'Request approved successfully',
      level: notificationStates.success,
    });

    // Refresh manager requests
    yield put({
      type: regularizationActionTypes.GET_MANAGER_REQUESTS,
      payload: {
        managerId: requestData.assignee,
        companyId,
      },
    });

    result = { success: true, data };
    return result;
  } catch (error) {
    console.error('Error approving regularization request:', error);
    addNotification({
      message: formatRegularizationErrorMessage(
        error,
        'Failed to approve request'
      ),
      level: notificationStates.error,
    });
    result = { success: false, error: error.message, code: error.code };
    return result;
  } finally {
    if (typeof payload?.onComplete === 'function') {
      payload.onComplete(result ?? { success: false, error: 'Unknown error' });
    }
  }
}

/**
 * Decline a regularization request
 */
function* declineRequestSaga({ payload }) {
  let result;
  try {
    const { requestId, companyId, assigneeId } = payload;

    yield call(
      regularizationService.declineRequest,
      requestId,
      companyId,
      assigneeId
    );

    addNotification({
      message: 'Request declined successfully',
      level: notificationStates.success,
    });

    // Refresh manager requests
    yield put({
      type: regularizationActionTypes.GET_MANAGER_REQUESTS,
      payload: {
        managerId: assigneeId,
        companyId,
      },
    });

    result = { success: true };
    return result;
  } catch (error) {
    console.error('Error declining regularization request:', error);
    addNotification({
      message: formatRegularizationErrorMessage(
        error,
        'Failed to decline request'
      ),
      level: notificationStates.error,
    });
    result = { success: false, error: error.message, code: error.code };
    return result;
  } finally {
    if (typeof payload?.onComplete === 'function') {
      payload.onComplete(result ?? { success: false, error: 'Unknown error' });
    }
  }
}

export default function* regularizationWatcher() {
  yield all([
    takeLatest(regularizationActionTypes.GET_REQUESTS, getRequestsSaga),
    takeLatest(
      regularizationActionTypes.GET_MANAGER_REQUESTS,
      getManagerRequestsSaga
    ),
    takeLatest(regularizationActionTypes.CREATE_REQUEST, createRequestSaga),
    takeLatest(regularizationActionTypes.UPDATE_REQUEST, updateRequestSaga),
    takeLatest(regularizationActionTypes.DELETE_REQUEST, deleteRequestSaga),
    takeLatest(regularizationActionTypes.APPROVE_REQUEST, approveRequestSaga),
    takeLatest(regularizationActionTypes.DECLINE_REQUEST, declineRequestSaga),
  ]);
}
